/* eslint-disable class-methods-use-this */

'use strict';

define('vbc/private/trace/tracer',[
  'vbc/private/trace/spanContext',
  'vbc/private/constants',
  'vbc/private/utils',
], (SpanContext, Constants, Utils) => {
  // Promise that resolves when the tracer has been initialized
  let tracerPromise;
  let logger;
  // Because of a circular dependency between Trace and Log, logger needs to be loaded on demand
  const getLogger = () => {
    if (!logger) {
      const Log = requirejs('vbc/private/log');
      logger = Log.getLogger('/vbc/private/trace/tracer');
    }
    return logger;
  };

  const noop = () => {};
  const noopTracer = {
    startSpan: noop,
    span: (opt, fn) => fn(),
    finishSpan: noop,
    shutdown: noop,
  };

  let otelLogger = null;
  let SeverityNumber = null;
  let severityMap = {};

  /**
   * Initializes the OpenTelemetry logger by obtaining a logger instance from the global LoggerProvider.
   * Sets the `otelLogger` and `SeverityNumber` for later use.
   * Logs an error if the LoggerProvider is not available.
   * Creates severity Map using OtelApiLogRecord.SeverityNumber
   *
   * @param {Object} otelApiLogs - The OpenTelemetry API Logs module.
   * @param {Object} otelApiLogRecord - The OpenTelemetry API Log Record module.
   */
  function initializeOtelLogger(otelApiLogs, otelApiLogRecord) {
    const loggerProvider = otelApiLogs.LogsAPI.getInstance().getLoggerProvider();
    if (loggerProvider) {
      otelLogger = loggerProvider.getLogger('VBRT');
      SeverityNumber = otelApiLogRecord.SeverityNumber;
      severityMap = {
        [Constants.Severity.ERROR]: SeverityNumber.ERROR,
        [Constants.Severity.INFO]: SeverityNumber.INFO,
        [Constants.Severity.WARNING]: SeverityNumber.WARN,
        [Constants.Severity.FINE]: SeverityNumber.DEBUG,
        [Constants.Severity.FINER]: SeverityNumber.TRACE,
      };
    }
  }

  /**
   * Maps a given severity level from custom constants to the corresponding OpenTelemetry SeverityNumber.
   *
   * @param {string} severity - The severity level.
   * @returns {number} - The matching OpenTelemetry SeverityNumber value.
   */
  function findOtelSeverityNumber(severity) {
    return severityMap[severity] || SeverityNumber.UNSPECIFIED;
  }

  /**
   * Used for OpenTracing log record and span management
   */
  class Tracer {
    constructor() {
      // a default tracer is a noop
      this.reset();
    }

    /**
     * @param options tracer configuration - @see {@link Tracer#checkOptions}
     * @returns {Promise<any>} a promise to an initialized tracer. If tracer options pass a basic sanity check,
     * this will be a client trace Tracer. Otherwise this will be a noop tracer.
     * @see https://confluence.oraclecorp.com/confluence/display/MDO/Trace-Client+API
     */
    init(options) {
      if (!tracerPromise) {
        tracerPromise = Promise.resolve()
          .then(() => {
            if (!Tracer.checkOptions(options)) {
              this.isEnabled = false;
              getLogger().info('Tracer is disabled');
              return this;
            }
            return new Promise((resolve, reject) => {
              requirejs(
                ['otel/api-logs/api/logs', 'otel/api-logs/types/LogRecord'],
                (...loaded) => resolve(loaded),
                (err) => reject(err),
              );
            }).then(([OtelApiLogs, OtelApiLogRecord]) => {
              this.options = options;
              // a default tracer is a noop
              if (this.isTelemetryLoaded()) {
                // BUFP-42619: If the telemetry libs were loaded but the tracer
                // isn't initialized yet, it's possible this will return
                // null.  If so, enable tracing anyway and we'll check again
                // the first time a span is created.
                // eslint-disable-next-line
                void this.tracer; // Prefetch the globalTracer
                this.isEnabled = true;
                initializeOtelLogger(OtelApiLogs, OtelApiLogRecord);
              }
              getLogger().info('Tracer is enabled');
              return this;
            });
          })
          .catch((err) => {
            getLogger().error('Failed to initialize tracer', err);
            this.reset();
          });
      }
      return tracerPromise;
    }

    /**
     * Ensure the telemetry lib is loaded
     */
    isTelemetryLoaded() {
      return globalThis.GlobalTracer !== undefined;
    }

    /**
     * Resolve the tracer object if it wasn't resolved by init()
     * @see https://jira.oraclecorp.com/jira/browse/BUFP-42619
     */
    get tracer() {
      if (this.isTelemetryLoaded()) {
        if (!this.globalTracer) {
          // Get the global tracer
          const tracer = globalThis.GlobalTracer.get();
          if (tracer) {
            // Cache if tracer was available
            this.globalTracer = tracer;
          } else {
            // Return noopTracer if tracer not loaded
            return noopTracer;
          }
        }
        return this.globalTracer;
      }
      // Return noopTracer if no telemetry
      return noopTracer;
    }

    /**
     * Resets tracer singleton so it can be reinitialized.  Note that this will not affect the state of the
     * underlying tracer (since JET et al. may be using it), just resets and decouples the VB wrapper.
     */
    reset() {
      tracerPromise = null;
      this.options = null;
      this.globalTracer = null;
      this.isEnabled = false;
    }

    /**
     * @param options tracer configuration, containing:
     * <li>disabled</li> true, if tracing should be disabled for this application
     * <li>injectEnabled</li> true if the tracer should inject span context in outgoing REST calls
     * @see https://confluence.oraclecorp.com/confluence/display/MDO/FusionApps+Client+Logging+Configuration
     * @returns {boolean} true, if trace options pass a basic sanity check. False otherwise.
     */
    static checkOptions(options) {
      if (options && options.disabled) {
        getLogger().info('Tracer options check: tracing is disabled', options);
        return false;
      }
      return true;
    }

    /**
     * Creates and returns a client trace span.  Typically it is preferable to use span() instead
     * @param {Object} spanContext the SpanContext for the span
     * @returns {Object} span, if a valid span context was provided. Otherwise returns undefined.
     */
    startSpan(spanContext) {
      let span;
      if (this.isEnabled && spanContext instanceof SpanContext) {
        try {
          const spanOptions = spanContext.startSpanOptions();
          if (spanOptions) {
            span = this.tracer.startSpan(spanOptions);
          }
        } catch (err) {
          getLogger().error('Failed to start trace span', spanContext, err);
        }
      }
      return span;
    }

    /**
     * Ends specified client trace span. If span is undefined, this is a noop.
     * @param {Object} span the span to close
     * @param {Object} spanContext the SpanContext for the span
     */
    finishSpan(span, spanContext) {
      try {
        if (this.isEnabled && span) {
          let spanOptions;
          if (spanContext instanceof SpanContext) {
            spanOptions = spanContext.endSpanOptions();
          }
          span.finish(spanOptions);
        }
      } catch (err) {
        getLogger().error('Failed to finish trace span', span, err);
      }
    }

    /**
     * Executes a span with the given options, executing the spanContext.spanFunction within
     * a span's context and returning its return value, if any
     * @param {Object} spanContext Span context object
     * @returns The return value of the spanContext.spanFunction() property
     */
    span(spanContext) {
      if (spanContext instanceof SpanContext) {
        if (this.isEnabled) {
          try {
            const spanOptions = spanContext.startSpanOptions() || {};
            const fn = spanContext.spanFunction();
            if (spanOptions && fn) {
              return this.tracer.span(spanOptions, fn);
            }
          } catch (err) {
            getLogger().error('Failed to start trace span', spanContext, err);
          }
        }

        // If disabled or error occurred, let function passthrough
        const fn = spanContext.spanFunction();
        if (fn) {
          return fn();
        }
      }
      return undefined;
    }

    /**
     * Emits a log record to OpenTelemetry with the specified severity and log arguments.
     *
     * Formats the provided log arguments, constructs a log record, and emits it using the initialized
     * OpenTelemetry logger.
     *
     * If the logger is not initialized, it logs a warning instead.
     *
     * @param {string} severity - The severity level of the log
     * @param {...any} logArgs - The log arguments to format and include in the log record body.
     */
    emitOtelLogs(severity, ...logArgs) {
      if (!otelLogger) {
        getLogger().warn('otelLogger not initialized. Skipping Otel log emit.');
        return;
      }
      const logRecord = {
        body: Utils.formatArguments(...logArgs),
        severityNumber: findOtelSeverityNumber(severity),
        severityText: severity,
      };
      otelLogger.emit(logRecord);
    }
  }

  // return a singleton tracer
  return new Tracer();
});

